import { handleSubmitSupportRequest } from '@/entities/support';
import { INVALID_EMAIL_ERROR_MESSAGE, LOADING_TYPES } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { showMessageError, showMessageSuccess, validateEmail } from '@/shared/lib';
import _ from 'lodash';
import { useEffect } from 'react';
import { t } from 'ttag';
import type { BillingQuestionsProps, BillingQuestionsState } from './billingQuestions.types';

/**
 * Holds the form state of the billing questions support request: reply emails, message, selected billing question and errors.
 * Clears the form whenever the selected facility changes and exposes handlers that drop field
 * errors on edit and submit the request through handleSubmitSupportRequest.
 *
 * @param props - Form props: category, selectedFacility, setLoadingPage and backToPrevious.
 * @returns An object carrying the form state and the change, send, email-list and email-input
 *   handlers.
 */
export const useBillingQuestions = ({
  category,
  selectedFacility,
  setLoadingPage,
  backToPrevious,
}: BillingQuestionsProps) => {
  const [state, setState] = useMergeState<BillingQuestionsState>({
    emailList: [],
    emailInputValue: '',
    message: '',
    selectedBillingQuestion: null,
    errorObject: {},
    isSaving: false,
  });

  /**
   * Records one answer of the support request form and drops the error attached to that field. Deep-clones errorObject, deletes the entry keyed by the field name and stores both the value and the trimmed error map.
   *
   * @param name - Key of the support request field being answered.
   * @param value - Value entered for that field.
   * @returns Nothing.
   */
  const onChange = (name: any, value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject[name];
    setState({ [name]: value, errorObject: cloneErrorObject });
  };

  /**
   * Submits a billing support request for the selected facility. Rejects a half-typed reply-to address through validateEmail and stores an invalidEmails error, otherwise shows the submitting page loader, posts category, the joined reply emails, the selected billing question, the trimmed message, appName 'Bioflux' and the facility id through handleSubmitSupportRequest, then confirms with a billing-department success toast and navigates back, or logs and reports a send failure.
   *
   * @returns A promise settling once the request was sent and the page navigated back, or the failure reported.
   */
  const onClickSendMessage = async () => {
    setState({ isSaving: true });
    const errorObject = {};
    const isValidCurrentInput = !state.emailInputValue || validateEmail(state.emailInputValue);
    if (!isValidCurrentInput) {
      _.assign(errorObject, { invalidEmails: INVALID_EMAIL_ERROR_MESSAGE });
    }
    if (!_.isEmpty(errorObject)) {
      setState({
        errorObject,
        isSaving: false,
      });
    } else {
      try {
        setLoadingPage(LOADING_TYPES.SUBMITTING);
        await handleSubmitSupportRequest({
          category,
          replyEmail: _.join(state.emailList, ','),
          issues: [state.selectedBillingQuestion],
          message: state.message.trim(),
          appName: 'Bioflux',
          facilityId: selectedFacility?.value as string,
        });
        showMessageSuccess({
          content: t`We have received your request and someone from our billing department will be reaching out to you shortly`,
        });
        setLoadingPage(false);
        backToPrevious();
      } catch (error) {
        console.error('Failed to send support email: ', error);
        showMessageError({ content: t`Failed to send your message` });
        setState({
          isSaving: false,
        });
      }
    }
  };

  /**
   * Stores the reply-to email addresses collected by the support request form. Deep-clones errorObject without its invalidEmails entry, then saves the new list and empties the pending email input.
   *
   * @param value - Accepted email addresses of the reply-to list.
   * @returns Nothing.
   */
  const onChangeEmailList = (value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.invalidEmails;
    setState({ emailList: value, emailInputValue: '', errorObject: cloneErrorObject });
  };

  /**
   * Tracks the half-typed reply-to address of the support request form. Deep-clones errorObject without its invalidEmails entry and stores the raw input text alongside it.
   *
   * @param value - Current text of the email input.
   * @returns Nothing.
   */
  const onInputChange = (value: any) => {
    const cloneErrorObject = _.cloneDeep(state.errorObject);
    delete cloneErrorObject.invalidEmails;
    setState({ emailInputValue: value, errorObject: cloneErrorObject });
  };

  useEffect(() => {
    setState({
      emailList: [],
      emailInputValue: '',
      message: '',
      selectedBillingQuestion: null,
      errorObject: {},
    });
  }, [selectedFacility]);

  return {
    state,
    onChange,
    onClickSendMessage,
    onChangeEmailList,
    onInputChange,
  };
};
